import { useChatContext } from '@/context/ChatProvider'
import {
  collectFormConfigs,
  getFormConfig,
  getFormId,
  getSubmittedFormFields,
  normalizeFormHistoryMessage,
  parseFormObject
} from '@/lib/form-message-history'
import { acknowledgeFormSubmission, mergeHydratedFormMessages, rejectFormSubmission } from '@/lib/form-message-state'
import { getOperatorAvatarLabel, normalizeOperatorName } from '@/lib/live-support'
import { useNotificationTranslator } from '@/lib/notifications'
import { useChatStore, type ChatMessage, type SubmittedFormFields } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import { receiveReviewPrompt } from '@/lib/review-prompt'
import { createNotificationHandlers } from './notificationStateHandlers'
import {
  mergeConversationMessage,
  mergeMigratedConversationHistory,
  replaceConversationHistory
} from '@/lib/conversation-message-state'

const getPayloadObject = (data: any): Record<string, any> => {
  const contentObject = parseFormObject(data?.content)
  const dataObject = data?.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : null

  return {
    ...(dataObject ?? {}),
    ...(contentObject ?? {}),
    ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {})
  }
}

const normalizeHistoryComponentMessage = (msg: any): ChatMessage | null => {
  const contentObject = parseFormObject(msg.content)
  const payload = contentObject?.type === 'component' ? contentObject : msg?.type === 'component' ? msg : null
  if (!payload?.component_id) return null

  return {
    role: 'assistant',
    content: payload,
    type: 'component',
    _id: msg._id,
    timestamp: msg.timestamp,
    sessionId: msg.session_id
  }
}

const normalizeHistoryArticleMessage = (msg: any): ChatMessage | null => {
  const contentObject = parseFormObject(msg.content)
  const payload = contentObject?.type === 'article' ? contentObject : msg?.type === 'article' ? msg : null
  if (msg.role !== 'article' && !payload) return null
  if (!payload?.article_id && !payload?.body_markdown) return null

  return {
    role: 'assistant',
    content: payload,
    type: 'article',
    _id: msg._id,
    timestamp: msg.timestamp,
    sessionId: msg.session_id
  }
}

export const useMessageHandlers = () => {
  const setMessages = useChatStore(state => state.setMessages)
  const setCurrentPage = useChatStore(state => state.setCurrentPage)
  const setTotalPages = useChatStore(state => state.setTotalPages)
  const setIsLoadingHistory = useChatStore(state => state.setIsLoadingHistory)
  const setHasMoreHistory = useChatStore(state => state.setHasMoreHistory)
  const setIsSecondChatShown = useChatStore(state => state.setIsSecondChatShown)
  const setOperatorName = useChatStore(state => state.setOperatorName)
  const setLiveSupportState = useChatStore(state => state.setLiveSupportState)
  const setPendingReview = useChatStore(state => state.setPendingReview)
  const setIsGuestInfoRequired = useChatStore(state => state.setIsGuestInfoRequired)

  const { scrollToBottom } = useChatContext()
  const translateNotification = useNotificationTranslator()

  const setMessagesWithoutThinking = (newMessage: ChatMessage | ChatMessage[], shouldScroll = true) => {
    setMessages(prev => {
      const filtered = prev.filter(m => !m.isThinking)
      const messages = Array.isArray(newMessage) ? newMessage : [newMessage]

      return messages.reduce((current, message) => mergeConversationMessage(current, message), filtered)
    })

    if (shouldScroll) {
      scrollToBottom()
    }
  }

  const handleMessageHistory = (data: any) => {
    const messageHistory = data?.messages || []
    const metadata = data?.metadata || {}

    if (metadata.current_page === 1) {
      scrollToBottom({ behavior: 'instant' })
    }

    // Update pagination state
    setCurrentPage(metadata.current_page || 1)
    setTotalPages(metadata.total_pages || 1)
    setHasMoreHistory((metadata.current_page || 1) < (metadata.total_pages || 1))
    setIsLoadingHistory(false)

    let historyOperatorName: string | null = null

    const retainedFormConfigs = collectFormConfigs(messageHistory)

    const formattedMessages = messageHistory
      ?.filter((msg: any) => msg.role !== 'system')
      .map((msg: any) => {
        if (msg.role === 'notification') {
          const notificationData = {
            key: msg.notif_key,
            message: msg.content,
            values: msg.notif_values
          }

          if (msg.notif_key === 'operator_joined') {
            historyOperatorName = normalizeOperatorName(msg.notif_values?.operator_name)
          }

          if (
            msg.notif_key === 'operator_left' ||
            msg.notif_key === 'operator_released' ||
            msg.notif_key === 'resolved'
          ) {
            historyOperatorName = null
          }

          return {
            role: 'assistant' as const,
            content: msg.notif_key ? translateNotification(notificationData) : msg.content,
            type: 'notification' as const,
            _id: msg._id,
            timestamp: msg.timestamp,
            sessionId: msg.session_id
          }
        }

        const formMessage = normalizeFormHistoryMessage(msg, retainedFormConfigs)
        if (formMessage) return formMessage
        if (msg.role === 'form' || msg.type === 'show_form' || msg.type === 'form_submission') return null

        const componentMessage = normalizeHistoryComponentMessage(msg)
        if (componentMessage) return componentMessage

        const articleMessage = normalizeHistoryArticleMessage(msg)
        if (articleMessage) return articleMessage

        const operatorMessageName = normalizeOperatorName(msg.operator_name) ?? historyOperatorName
        const localizedContent = msg.notif_key
          ? translateNotification({
              key: msg.notif_key,
              message: msg.content,
              values: msg.notif_values
            })
          : msg.content

        return {
          role: msg.role,
          content: localizedContent,
          type: 'message' as const,
          _id: msg._id,
          client_message_id: msg.client_message_id,
          timestamp: msg.timestamp,
          sessionId: msg.session_id,
          operatorId: msg.operator_id,
          ...(msg.operator_id ? { operatorAvatarLabel: getOperatorAvatarLabel(operatorMessageName) } : {}),
          ...(msg.operator_id && operatorMessageName ? { operatorName: operatorMessageName } : {}),
          ...(msg.metadata?.ticketId ? { ticketId: msg.metadata.ticketId } : {})
        }
      })
      .filter((message: ChatMessage | null): message is ChatMessage => message !== null)

    setMessages(prev => {
      const firstPage = metadata.current_page === 1
      if (data.migration) return mergeMigratedConversationHistory(prev, formattedMessages)
      const hydrated = mergeHydratedFormMessages(
        firstPage && !data.migration ? [] : prev,
        formattedMessages
      ) as ChatMessage[]

      return firstPage && !data.migration
        ? replaceConversationHistory(prev, hydrated, data?.sessionId ?? usePersistStore.getState().sessionId)
        : hydrated
    })
  }

  const handleResponse = (data: any) => {
    const content = data?.notif_key
      ? translateNotification({
          key: data.notif_key,
          message: data.content,
          values: data.notif_values
        })
      : data.content
    setMessagesWithoutThinking({
      role: 'assistant',
      content,
      type: 'message',
      _id: data?._id,
      sessionId: data?.session_id,
      timestamp: data?.timestamp ?? Date.now(),
      ...(data?.metadata?.ticketId ? { ticketId: data.metadata.ticketId } : {})
    })

    // Ticket updates are delivered as response frames; let a mounted tickets
    // panel (list or open detail) refetch itself.
    if (data?.metadata?.ticketId) {
      useChatStore.getState().bumpTicketsRefreshKey()
    }
  }

  const handleUserMessage = (data: any) => {
    if (data.session_id !== usePersistStore.getState().sessionId || !data._id || typeof data.content !== 'string')
      return
    setMessages(prev =>
      mergeConversationMessage(prev, {
        role: 'user',
        type: 'message',
        content: data.content,
        _id: data._id,
        client_message_id: data.client_message_id,
        timestamp: data.timestamp,
        sessionId: data.session_id
      })
    )
    scrollToBottom()
  }

  const handleError = (data: any) => {
    setMessagesWithoutThinking({
      role: 'assistant',
      content: `Error: ${data.content || data.message}`,
      type: 'error',
      timestamp: data?.timestamp ?? Date.now()
    })
  }

  const handleAskUser = (data: any) => {
    setMessagesWithoutThinking({
      role: 'assistant',
      content: `Assistant asks: ${data.content}`,
      type: 'info',
      timestamp: data?.timestamp ?? Date.now()
    })
  }

  const handleEndOfStream = (data: any) => {
    // TODO: Handle end of stream
  }

  const handleSectionSelectAck = (data: any) => {
    setMessagesWithoutThinking({
      role: 'assistant',
      content: data.content,
      type: 'info',
      timestamp: data?.timestamp ?? Date.now()
    })
  }

  const handleShowForm = (data: any) => {
    const formConfig = getFormConfig(data, parseFormObject(data?.content))
    if (!formConfig) return

    setMessagesWithoutThinking({
      role: 'assistant',
      content: {
        ...formConfig,
        answered: false,
        submitted: false
      },
      type: 'show_form',
      timestamp: data?.timestamp ?? Date.now()
    })
  }

  const handleComponent = (data: any) => {
    if (data?.component_id === 'guest_info_request') {
      setIsGuestInfoRequired(true)
    }

    setMessagesWithoutThinking({
      role: 'assistant',
      content: data,
      type: 'component',
      timestamp: data?.timestamp ?? Date.now()
    })
  }

  const handleArticle = (data: any) => {
    setMessagesWithoutThinking({
      role: 'assistant',
      content: data,
      type: 'article',
      timestamp: data?.timestamp ?? Date.now()
    })
  }

  const handleGuestInfoSubmissionAck = (data: any) => {
    setIsGuestInfoRequired(false)

    setMessages(prev =>
      prev.map(message => {
        if (message.type !== 'component' || !message.content || typeof message.content !== 'object') return message

        const content = message.content as Record<string, unknown>
        if (content.component_id !== 'guest_info_request') return message

        return {
          ...message,
          content: {
            ...content,
            data: {
              ...((content.data as Record<string, unknown>) ?? {}),
              submitted: true,
              guest_info: data?.guest_info
            }
          }
        }
      })
    )
  }

  const handleGuestInfoSubmissionError = (data: any) => {
    setMessages(prev =>
      prev.map(message => {
        if (message.type !== 'component' || !message.content || typeof message.content !== 'object') return message

        const content = message.content as Record<string, unknown>
        if (content.component_id !== 'guest_info_request') return message

        return {
          ...message,
          content: {
            ...content,
            data: {
              ...((content.data as Record<string, unknown>) ?? {}),
              submitted: false,
              submission_error: data?.reason || 'guest_info_submission_error'
            }
          }
        }
      })
    )
  }

  const handleFormSubmissionAck = (data: any) => {
    const payload = getPayloadObject(data)
    const formId = getFormId(payload)
    if (!formId) return

    const submittedFields = getSubmittedFormFields(payload, payload)
    const answerId = payload.answer_id ?? payload.answerId
    const isFinalAck = payload.queued === false && payload.persisted === true && payload.final === true

    if (!isFinalAck) {
      setMessages(
        prev =>
          rejectFormSubmission(prev, {
            formId,
            answerId,
            message: 'Form submission is still being saved. Please try again.'
          }) as ChatMessage[]
      )

      return
    }

    setMessages(
      prev =>
        acknowledgeFormSubmission(prev, {
          formId,
          answerId,
          submittedFields: submittedFields as SubmittedFormFields | null,
          queued: payload.queued,
          persisted: payload.persisted,
          final: payload.final,
          submittedFieldsRedacted: payload.submitted_fields_redacted === true
        }) as ChatMessage[]
    )
  }

  const handleFormSubmissionError = (data: any) => {
    const payload = getPayloadObject(data)
    const formId = getFormId(payload)
    if (!formId) return

    const message =
      payload.message ||
      payload.error ||
      payload.reason ||
      payload.content ||
      'Form submission could not be saved. Please try again.'

    setMessages(
      prev =>
        rejectFormSubmission(prev, {
          formId,
          answerId: payload.answer_id ?? payload.answerId,
          message: String(message)
        }) as ChatMessage[]
    )
  }

  const handleNewOperatorMessage = (data: any) => {
    setMessagesWithoutThinking({
      role: 'system',
      content: '',
      type: 'separator'
    })
  }

  const handleInfo = (data: any) => {
    setMessagesWithoutThinking({
      role: 'assistant',
      content: data.message,
      type: 'info',
      timestamp: data?.timestamp ?? Date.now()
    })
  }

  const handleTransfer = (data: any) => {
    if (data.data === 'live-support') {
      setIsSecondChatShown(true)
    }
  }

  const handleOperatorMessage = (data: any) => {
    const operatorMessageName = normalizeOperatorName(data?.operator_name) ?? useChatStore.getState().operatorName

    setMessagesWithoutThinking({
      _id: data?.message_id,
      client_message_id: data?.client_message_id,
      role: 'user',
      content: data?.content || '',
      type: 'message',
      operatorId: data?.operator_id,
      timestamp: data?.timestamp ?? Date.now(),
      ...(data?.operator_id ? { operatorAvatarLabel: getOperatorAvatarLabel(operatorMessageName) } : {}),
      ...(data?.operator_id && operatorMessageName ? { operatorName: operatorMessageName } : {})
    })
  }

  const handleReviewPrompt = (data: unknown) => {
    const { sessionId, reviewedSessions } = usePersistStore.getState()
    const prompt = receiveReviewPrompt(data, sessionId, reviewedSessions)
    if (prompt && useChatStore.getState().pendingReview?.session_id !== prompt.session_id) setPendingReview(prompt)
  }

  const handleNotification = (data: any) => {
    const handlers = createNotificationHandlers(data, {
      setOperatorName,
      setLiveSupportState
    })

    handlers[data?.key]?.()

    setMessagesWithoutThinking({
      role: 'assistant',
      content: translateNotification(data),
      type: 'notification'
    })
  }

  const handleTypingIndicator = (data: any) => {
    const isTyping = data?.data === true || data === true

    if (isTyping) {
      setMessages(prev => {
        if (prev.some(m => m.isThinking)) return prev

        return [
          ...prev,
          {
            role: 'assistant',
            content: '',
            isThinking: true
          }
        ]
      })
      scrollToBottom()
    } else {
      setMessages(prev => prev.filter(m => !m.isThinking))
    }
  }

  return {
    handleMessageHistory,
    handleResponse,
    handleUserMessage,
    handleError,
    handleAskUser,
    handleEndOfStream,
    handleSectionSelectAck,
    handleShowForm,
    handleComponent,
    handleArticle,
    handleGuestInfoSubmissionAck,
    handleGuestInfoSubmissionError,
    handleFormSubmissionAck,
    handleFormSubmissionError,
    handleNewOperatorMessage,
    handleInfo,
    handleTransfer,
    handleOperatorMessage,
    handleNotification,
    handleReviewPrompt,
    handleTypingIndicator
  }
}
